<template>
  <div>
    <div class="flex flex-col gap-4">
      <h3>{{ t('reasonForCarSwap') }}</h3>
      <div class="grid gap-4 lg:grid-cols-3">
        <SelectField
          :name="`allocations.${props.allocationIndex}.carSwapReason`"
          :options="carSwapReasonOptions"
          :label="t('reasonForCarSwap')"
        />
        <TextareaField
          :name="`allocations.${props.allocationIndex}.comment`"
          :label="t('comment')"
          :rows="3"
          class="col-span-3"
        />
      </div>
    </div>

    <Divider />

    <div class="flex justify-between gap-2">
      <CVButton @click="emit('back')">{{ t('back') }}</CVButton>
      <div class="flex flex-1 justify-end gap-2">
        <CVButton
          variant="default"
          :disabled="!readyToSave"
          :is-loading="isSaving"
          @click="emit('save')"
          >{{ t('save') }}</CVButton
        >
        <CVButton :disabled="isSaving" @click="handleCancel">{{ t('cancel') }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useField } from 'vee-validate';
import { CarSwapReason } from '@carvia/ros-client-types';
import CVButton from '../buttons/CVButton.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import Divider from '../Divider.vue';
import SelectField from '../SelectField.vue';
import TextareaField from '../TextareaField.vue';

const props = defineProps<{
  booking: Booking;
  allocationIndex: number;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'save'): void;
  (e: 'back'): void;
}>();

const { t } = useI18n();

const { value: carSwapReason } = useField<CarSwapReason>(
  `allocations.${props.allocationIndex}.carSwapReason`,
);
const { value: comment } = useField<string | undefined>(
  `allocations.${props.allocationIndex}.comment`,
);
const carSwapReasonOptions = computed(() =>
  Object.entries(CarSwapReason).map(([key, value]) => ({
    label: t(`carSwapReasons.${key}`),
    value,
  })),
);

const readyToSave = computed(
  () =>
    carSwapReason.value && (carSwapReason.value !== CarSwapReason.OTHER || comment.value?.trim()),
);

const handleCancel = () => {
  emit('cancel');
};
</script>

<i18n lang="json">
{
  "en": {
    "reasonForCarSwap": "Reason for car swap",
    "save": "Save",
    "comment": "Comment"
  },
  "de": {
    "reasonForCarSwap": "Grund für Autotausch",
    "save": "Speichern",
    "comment": "Kommentar"
  },
  "es": {
    "reasonForCarSwap": "Motivo del cambio de vehículo",
    "save": "Guardar",
    "comment": "Comentario"
  }
}
</i18n>
